import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { SYM } from '../../Components/MaterialSymbol';

export type SpeechToTextProps = {
  disabled?: boolean;
  onTextReceived: (text: string) => void;
  onListeningChange?: (listening: boolean) => void;
};

export const SpeechToText: FC<SpeechToTextProps> = ({ disabled = false, onTextReceived, onListeningChange }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const onToggleListening = useCallback(() => setListening((prev) => !prev), []);
  const cleanUpRecognition = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!disabled) {
      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (SpeechRecognition && recognitionRef.current === null) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map((result) => result[0].transcript)
            .join('');
          onTextReceived(transcript);
        };
      }
    }

    return cleanUpRecognition;
  }, [disabled, onTextReceived]);

  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = () => {
        if (!disabled && listening) {
          recognitionRef.current?.start();
        }
      };

      if (listening) {
        recognitionRef.current.start();
      } else {
        recognitionRef.current.stop();
      }
    }

    onListeningChange?.(listening);
  }, [disabled, listening, onListeningChange]);

  return (
    <button disabled={disabled} type="button" onClick={onToggleListening}>
      <SYM style={{ opacity: listening ? 1 : 0.5 }}>mic</SYM>
    </button>
  );
};
